import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, ToggleButtonGroup, ToggleButton, Card, CardContent,
  IconButton, Dialog, DialogTitle, DialogActions, Button, Alert, Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';
import { API_CONFIG } from '../config';

interface WeightData {
  date: string;
  weight: number;
}

type TimeRange = '1W' | '1M' | '3M' | '6M' | 'ALL';

interface WeightGraphProps {
  targetWeight?: number;
  refreshKey?: number;
}

const WeightGraph: React.FC<WeightGraphProps> = ({ targetWeight, refreshKey }) => {
  const [weightData, setWeightData] = useState<WeightData[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('1M');
  const { t } = useLanguage();
  const theme = useTheme();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDate, setDeleteDate] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await api.get('/weight');
      setWeightData(response.data);
    } catch (error) {
      console.error('Failed to fetch weight data:', error);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshKey]);

  const filteredData = React.useMemo(() => {
    if (timeRange === 'ALL') return weightData;
    const now = new Date();
    const daysMap: Record<TimeRange, number> = { '1W': 7, '1M': 30, '3M': 90, '6M': 180, 'ALL': 0 };
    const cutoff = new Date(now.getTime() - daysMap[timeRange] * 86400000);
    return weightData.filter(d => new Date(d.date) >= cutoff);
  }, [weightData, timeRange]);

  const handleDeleteWeight = async () => {
    if (!deleteDate) return;
    setDeleting(true);
    try {
      await api.delete(API_CONFIG.endpoints.weightEntry(deleteDate));
      setSuccess(t.weightDeleted);
      setDeleteDate(null);
      fetchHistory();
    } catch {
      setError(t.weightDeleteFailed);
    } finally {
      setDeleting(false);
    }
  };

  const startWeight = weightData.length > 0 ? weightData[0].weight : 0;
  const currentWeight = weightData.length > 0 ? weightData[weightData.length - 1].weight : 0;
  const totalChange = currentWeight - startWeight;

  const yMin = Math.min(
    ...filteredData.map(d => d.weight),
    targetWeight || Infinity
  ) - 1;
  const yMax = Math.max(
    ...filteredData.map(d => d.weight),
    targetWeight || -Infinity
  ) + 1;

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2">{t.weightOverTime}</Typography>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(_, v) => v && setTimeRange(v)}
            size="small"
          >
            {(['1W', '1M', '3M', '6M', 'ALL'] as TimeRange[]).map(r => (
              <ToggleButton key={r} value={r} sx={{ px: 1, py: 0.25, fontSize: 11 }}>
                {r}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {filteredData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={[Math.floor(yMin), Math.ceil(yMax)]} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="weight"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              {targetWeight && (
                <ReferenceLine
                  y={targetWeight}
                  stroke={theme.palette.secondary.main}
                  strokeDasharray="5 5"
                  label={{ value: `${t.target}: ${targetWeight} kg`, position: 'right', fontSize: 10, fill: theme.palette.secondary.main }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t.noWeightData}
          </Typography>
        )}

        {weightData.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{t.start}</Typography>
              <Typography variant="body2" fontWeight="bold">{startWeight} kg</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{t.current}</Typography>
              <Typography variant="body2" fontWeight="bold">{currentWeight} kg</Typography>
            </Box>
            {targetWeight && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{t.target}</Typography>
                <Typography variant="body2" fontWeight="bold">{targetWeight} kg</Typography>
              </Box>
            )}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{t.change}</Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={totalChange <= 0 ? 'success.main' : 'error.main'}
              >
                {totalChange > 0 ? '+' : ''}{totalChange.toFixed(1)} kg
              </Typography>
            </Box>
          </Box>
        )}

        {filteredData.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 2 }}>
            {filteredData.slice().reverse().map(entry => (
              <Box key={entry.date} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                <Typography variant="body2">{entry.date}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold">{entry.weight} kg</Typography>
                  <IconButton size="small" color="error" onClick={() => setDeleteDate(entry.date)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteDate} onClose={() => setDeleteDate(null)}>
        <DialogTitle>{t.confirmDeleteWeight}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDate(null)}>{t.cancel}</Button>
          <Button color="error" onClick={handleDeleteWeight} disabled={deleting}>
            {t.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default WeightGraph;
